import { createRef, Fragment } from "react";
import { Form, Formik } from "formik";
import { apiGet, apiPut, apiPost, messageFromException } from "helpers/Request";
import { transform as tagTransformer } from "schemas/Tag";
import { transform as collectionTransformer } from "schemas/Collection";
import AutocompleteInput from "components/FormElements/AutocompleteInput";
import SelectBox from "components/FormElements/SelectBox";
import { toast } from "react-hot-toast";
import { modal } from "components/HeadlessModal";
import PrimaryButton from "components/Buttons/PrimaryButton";
import SecondaryButton from "components/Buttons/SecondaryButton";
import DangerButton from "components/Buttons/DangerButton";
import { PlusIcon } from "@heroicons/react/solid";
import FastTextInput from "components/FormElements/FastTextInput";

function handleError(title, message) {
    modal.error({
        title,
        children: message,
        buttons: () => (
            <DangerButton onClick={modal.dismiss}>Okay</DangerButton>
        ),
    });
}

export function onRequestTag({ loadContent, selectedItems, auth }) {
    const formRef = createRef();
    modal({
        title: "Tag selected items",
        children: (
            <Formik
                initialValues={{ tags: [] }}
                innerRef={formRef}
                onSubmit={(values) => {
                    const data = {
                        tags: values.tags.map((tag) => tag.value),
                    };
                    modal.update({
                        disabled: true,
                    });
                    const id = toast.loading("Tagging...");
                    const promises = selectedItems.map((uuid) =>
                        apiPut(`/v4/content/${uuid}`, auth.publication, {
                            tags: data.tags,
                            acquire_lock: true,
                            append_tags: true,
                        })
                    );
                    Promise.all(promises)
                        .then(() => {
                            modal.dismiss();
                            toast.success("Content updated", { id: id });
                            loadContent();
                        })
                        .catch((err) => {
                            modal.dismiss();
                            toast.dismiss(id);
                            handleError(
                                "Unable to tag selected items",
                                messageFromException(err)
                            );
                        });
                }}
            >
                <Form>
                    <AutocompleteInput
                        label="Search tags:"
                        id="tags"
                        name="tags"
                        onRequestSearch={async (term) => {
                            const items = await apiGet(
                                "/v4/search",
                                auth.publication,
                                {
                                    keyword: term,
                                    per_page: 5,
                                    type: "tag",
                                }
                            );

                            return tagTransformer(items).map((item) => {
                                return {
                                    label: item.name,
                                    value: item.uuid,
                                };
                            });
                        }}
                    />
                    <div className="my-10">
                        <p className="prose-sm text-gray-400">
                            Tags will be appended to content, existing tags will
                            not be replaced.
                        </p>
                    </div>
                </Form>
            </Formik>
        ),
        buttons: ({ disabled }) => (
            <Fragment>
                <PrimaryButton
                    disabled={disabled}
                    onClick={() => {
                        formRef.current.submitForm();
                    }}
                >
                    Update Tags
                </PrimaryButton>
                <SecondaryButton disabled={disabled} onClick={modal.dismiss}>
                    Cancel
                </SecondaryButton>
            </Fragment>
        ),
    });
}

export function onRequestWorkflow({
    loadContent,
    selectedItems,
    workflows,
    auth,
}) {
    const formRef = createRef();
    modal({
        title: "Update Workflow",
        children: (
            <Formik
                initialValues={{
                    workflow: {
                        id: null,
                        label: "Select Workflow",
                    },
                }}
                innerRef={formRef}
                onSubmit={(values) => {
                    const promises = selectedItems.map((uuid) =>
                        apiPut(`/v4/content/${uuid}`, auth.publication, {
                            workflow_id: values.workflow.value,
                            acquire_lock: true,
                        })
                    );
                    const id = toast.loading("Updating workflow...");
                    modal.update({
                        disabled: true,
                    });
                    Promise.all(promises)
                        .then(() => {
                            modal.dismiss();
                            toast.success("Content updated", { id: id });
                            loadContent();
                        })
                        .catch((err) => {
                            modal.dismiss();
                            toast.dismiss(id);
                            handleError(
                                "Unable to update workflow",
                                messageFromException(err)
                            );
                        });
                }}
            >
                <Form>
                    <SelectBox
                        label="Workflow Status"
                        name="workflow"
                        id="workflow"
                        options={workflows.map((workflow) => {
                            return {
                                value: workflow.id,
                                label: workflow.name,
                            };
                        })}
                    />
                </Form>
            </Formik>
        ),
        buttons: ({ disabled }) => (
            <Fragment>
                <PrimaryButton
                    disabled={disabled}
                    onClick={() => {
                        formRef.current.submitForm();
                    }}
                >
                    Update Workflow
                </PrimaryButton>
                <SecondaryButton disabled={disabled} onClick={modal.dismiss}>
                    Cancel
                </SecondaryButton>
            </Fragment>
        ),
    });
}

export function onRequestCollection(selectedItems, auth, onCollectionCreated) {
    const collectionRef = createRef();
    modal({
        title: "Add to collections",
        children: (
            <Formik
                initialValues={{ collections: [] }}
                onSubmit={(v) => {
                    return onAddToCollection(v, selectedItems, auth);
                }}
                innerRef={collectionRef}
            >
                <Form>
                    <div className="flex">
                        <div className="flex-grow">
                            <AutocompleteInput
                                label="Search collections:"
                                id="collections"
                                name="collections"
                                onRequestSearch={(t) => {
                                    return onRequestSearchCollections(t, auth);
                                }}
                            />
                        </div>
                    </div>
                </Form>
            </Formik>
        ),
        buttons: () => (
            <Fragment>
                <PrimaryButton
                    onClick={() => {
                        collectionRef.current.submitForm();
                    }}
                >
                    Add to collection
                </PrimaryButton>
                <SecondaryButton
                    type="button"
                    onClick={() => {
                        modal.dismiss();
                        handleCreateCollection(
                            selectedItems,
                            auth,
                            onCollectionCreated
                        );
                    }}
                >
                    <PlusIcon className="h-4 w-4" />
                </SecondaryButton>
                <SecondaryButton onClick={modal.dismiss}>
                    Cancel
                </SecondaryButton>
            </Fragment>
        ),
    });
}

async function onRequestSearchCollections(term, auth) {
    const items = await apiGet("/v4/search", auth.publication, {
        keyword: term,
        per_page: 5,
        type: "collection",
    });

    return collectionTransformer(items).map((item) => {
        return {
            label: item.name,
            value: item.uuid,
        };
    });
}

function onAddToCollection(values, selectedItems, auth) {
    const data = {
        collections: values.collections.map((collection) => collection.value),
    };
    modal.update({
        disabled: true,
    });
    const id = toast.loading("Adding to collections...");

    const promises = selectedItems.map((uuid) =>
        apiPut(`/v4/content/${uuid}`, auth.publication, {
            collections: data.collections,
            acquire_lock: true,
            append_collections: true,
        })
    );

    Promise.all(promises)
        .then(() => {
            modal.dismiss();
            toast.success("Content updated", { id });
        })
        .catch((err) => {
            modal.dismiss();
            toast.dismiss(id);
            modal.error({
                title: "Unable to update content",
                children: messageFromException(err),
            });
        });
}

function handleCreateCollection(selectedItems, auth, callback) {
    const newCollectionRef = createRef();
    modal({
        title: "Add to new collection",
        children: (
            <Formik
                initialValues={{ name: "" }}
                innerRef={newCollectionRef}
                onSubmit={(v) => {
                    return onCreateCollection(v, selectedItems, auth, callback);
                }}
            >
                <Form>
                    <FastTextInput
                        name="name"
                        id="name"
                        placeholder="New Collection"
                    />
                </Form>
            </Formik>
        ),
        buttons: () => (
            <Fragment>
                <PrimaryButton
                    onClick={() => {
                        newCollectionRef.current.submitForm();
                    }}
                >
                    Create collection
                </PrimaryButton>
                <SecondaryButton onClick={modal.dismiss}>
                    Cancel
                </SecondaryButton>
            </Fragment>
        ),
    });
}

async function onCreateCollection(values, selectedItems, auth, callback) {
    const data = {
        name: values.name,
    };

    const id = toast.loading("Creating collection...");
    modal.update({ disabled: true });
    const collection = await apiPost(
        "/v4/collection/",
        auth.publication,
        data
    ).then((resp) => collectionTransformer(resp));

    const promises = selectedItems.map((uuid) =>
        apiPut(`/v4/content/${uuid}`, auth.publication, {
            collections: [collection.uuid],
            acquire_lock: true,
            append_collections: true,
        })
    );
    Promise.all(promises)
        .then(() => {
            modal.dismiss();
            toast.success("Content updated", { id });
            callback();
        })
        .catch((err) => {
            modal.dismiss();
            toast.dismiss(id);
            modal.error({
                title: "Unable to update content",
                children: messageFromException(err),
            });
        });
}
